import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { 
  BookOpen, Calendar,
  Video, Clock, CheckCircle, XCircle, AlertCircle,
  Play, Lock,
  FileText, ChevronDown, ChevronUp,
  Award, Sparkles, Brain, TrendingUp, History, ClipboardList
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { DashboardChat } from '../components/DashboardChat';
import { DashboardPractice } from '../components/DashboardPractice';
import { DashboardProgress } from '../components/DashboardProgress';
import { DashboardHistory } from '../components/DashboardHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECTS = ['GED Math', 'GED Science', 'GED Language Arts', 'SAT Math', 'SAT Reading', 'WorkKeys', 'College Algebra', 'Chemistry', 'Biology'];
const TOPICS = {
  'GED Math': ['Algebra', 'Geometry', 'Data Analysis', 'Number Operations'],
  'GED Science': ['Life Science', 'Physical Science', 'Earth Science'],
  'GED Language Arts': ['Reading Comprehension', 'Grammar', 'Essay Writing'],
  'SAT Math': ['Algebra', 'Geometry', 'Data Analysis', 'Advanced Math'],
  'SAT Reading': ['Main Ideas', 'Vocabulary', 'Evidence-Based Reading'],
  'WorkKeys': ['Applied Mathematics', 'Workplace Documents', 'Business Writing'],
  'College Algebra': ['Linear Equations', 'Quadratic Equations', 'Functions'],
  'Chemistry': ['Atomic Structure', 'Chemical Bonding', 'Reactions'],
  'Biology': ['Cell Biology', 'Genetics', 'Evolution']
};

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const { user, isSubscribed, refreshUser, credits, hasPlatformAccess, hasSessionCredits } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [progressSummary, setProgressSummary] = useState(null);
  const [progressRecords, setProgressRecords] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [watchedVideos, setWatchedVideos] = useState(new Set());
  const [watchSummary, setWatchSummary] = useState(null);

  // Booking state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('GED Math');
  const [sessionNotes, setSessionNotes] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSubject, setChatSubject] = useState('');

  // Practice Questions state
  const [practiceSubject, setPracticeSubject] = useState('SAT Math');
  const [practiceTopic, setPracticeTopic] = useState('Algebra');
  const [practiceDifficulty, setPracticeDifficulty] = useState('medium');
  const [practiceCount, setPracticeCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Check for subscription/package success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const isSubscription = searchParams.get('subscription');
    const packageType = searchParams.get('package');
    
    if (sessionId && (isSubscription || packageType)) {
      verifyPayment(sessionId, packageType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verifyPayment = async (sessionId, packageType) => {
    try {
      let endpoint;
      if (packageType) {
        endpoint = `${API_URL}/api/packages/verify/${sessionId}`;
      } else {
        endpoint = `${API_URL}/api/subscription/verify/${sessionId}`;
      }
      const { data } = await axios.get(endpoint, { withCredentials: true });
      if (data.payment_status === 'paid') {
        toast.success(packageType ? 'Purchase confirmed! Credits added. Book your session now!' : 'Subscription activated! Book your first session.');
        await refreshUser();
        setActiveTab('book');
        window.history.replaceState({}, '', '/dashboard?tab=book');
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (isSubscribed || hasPlatformAccess || hasSessionCredits) {
      fetchSessions();
      fetchSubscriptionStatus();
    }
    if (isSubscribed || hasPlatformAccess) {
      fetchVideos();
      fetchQuizzes();
      fetchProgress();
      fetchWatchProgress();
    }
  }, [isSubscribed, hasPlatformAccess, hasSessionCredits]);

  useEffect(() => {
    if (selectedDate && (isSubscribed || hasSessionCredits)) {
      fetchAvailability();
    }
  }, [selectedDate, isSubscribed, hasSessionCredits]);

  useEffect(() => {
    if (TOPICS[practiceSubject]?.length > 0) {
      setPracticeTopic(TOPICS[practiceSubject][0]);
    }
  }, [practiceSubject]);

  const fetchSessions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sessions`, { withCredentials: true });
      setSessions(data);
    } catch (error) {
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchVideos = async () => {
    setVideosLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/videos`, { withCredentials: true });
      setVideos(data);
    } catch (error) {
      if (error.response?.status !== 403) {
      }
    } finally {
      setVideosLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    setQuizzesLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/quizzes`, { withCredentials: true });
      setQuizzes(data);
    } catch (error) {
      if (error.response?.status !== 403) {
      }
    } finally {
      setQuizzesLoading(false);
    }
  };

  const startQuiz = (quiz) => {
    setActiveQuiz(quiz);
    setQuizAnswers({});
    setQuizResults(null);
  };

  const submitQuiz = async () => {
    if (!activeQuiz) return;
    setQuizSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/quizzes/${activeQuiz.id}/submit`,
        { answers: quizAnswers },
        { withCredentials: true }
      );
      setQuizResults(data);
      fetchProgress();
    } catch (error) {
      toast.error('Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  const fetchProgress = async () => {
    setProgressLoading(true);
    try {
      const [summaryRes, recordsRes] = await Promise.all([
        axios.get(`${API_URL}/api/progress/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/progress`, { withCredentials: true })
      ]);
      setProgressSummary(summaryRes.data);
      setProgressRecords(recordsRes.data);
    } catch (error) {
      if (error.response?.status !== 403) {
      }
    } finally {
      setProgressLoading(false);
    }
  };

  const getModuleProgress = (moduleName) => {
    return progressRecords.find(r => r.module === moduleName);
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`, { withCredentials: true });
      setSubscriptionStatus(data);
    } catch (error) {
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) return;
    setCancellingSubscription(true);
    try {
      await axios.post(`${API_URL}/api/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled');
      fetchSubscriptionStatus();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`${API_URL}/api/subscription/reactivate`, {}, { withCredentials: true });
      toast.success('Subscription reactivated!');
      fetchSubscriptionStatus();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const fetchWatchProgress = async () => {
    try {
      const [progressRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/watch-progress`, { withCredentials: true }),
        axios.get(`${API_URL}/api/watch-progress/summary`, { withCredentials: true })
      ]);
      setWatchedVideos(new Set(progressRes.data.map(r => r.video_id)));
      setWatchSummary(summaryRes.data);
    } catch (error) {
    }
  };

  const markVideoWatched = async (videoId) => {
    try {
      await axios.post(`${API_URL}/api/watch-progress/${videoId}`, {}, { withCredentials: true });
      setWatchedVideos(prev => new Set([...prev, videoId]));
      fetchWatchProgress();
    } catch (error) {
    }
  };

  const toggleModule = (moduleName) => {
    setExpandedModules(prev => ({ ...prev, [moduleName]: !prev[moduleName] }));
  };

  // Group videos by module
  const moduleData = useMemo(() => {
    const filtered = categoryFilter === 'all' ? videos : videos.filter(v => v.category === categoryFilter);
    const modules = {};
    filtered.forEach(v => {
      const mod = v.module || 'Uncategorized';
      if (!modules[mod]) modules[mod] = { videos: [], quiz: null, category: v.category };
      modules[mod].videos.push(v);
    });
    quizzes.forEach(q => {
      if (modules[q.module]) {
        modules[q.module].quiz = q;
      }
    });
    return modules;
  }, [videos, quizzes, categoryFilter]);

  const categories = useMemo(
    () => [...new Set(videos.map(v => v.category))],
    [videos]
  );

  const fetchAvailability = async () => {
    setSlotsLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const { data } = await axios.get(`${API_URL}/api/tutor/availability?date=${dateStr}`);
      setAvailableSlots(data.available_slots);
      setSelectedSlot('');
    } catch (error) {
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBookSession = async () => {
    if (!selectedSlot || !selectedSubject) {
      toast.error('Please select a time slot and subject');
      return;
    }

    setBookingLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sessions`,
        {
          date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedSlot,
          subject: selectedSubject,
          notes: sessionNotes
        },
        { withCredentials: true }
      );
      
      toast.success('Session booked! You\'ll receive a reminder 24 hours before.');
      setShowBookingDialog(false);
      setSelectedSlot('');
      setSessionNotes('');
      fetchSessions();
      fetchAvailability();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    } finally {
      setBookingLoading(false);
    }
  };

  // AI Chat functions
  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/chat`,
        { message: chatInput, subject: chatSubject || null },
        { withCredentials: true }
      );
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Requires a Weekly or Monthly package for AI assistant');
      } else {
        toast.error('Failed to get response. Please try again.');
      }
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  // Practice Questions functions
  const generateQuestions = async () => {
    setQuestionsLoading(true);
    setQuestions([]);
    setSelectedAnswers({});
    setShowResults(false);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/practice-questions`,
        {
          subject: practiceSubject,
          topic: practiceTopic,
          difficulty: practiceDifficulty,
          count: practiceCount
        },
        { withCredentials: true }
      );
      setQuestions(data.questions);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Requires a Weekly or Monthly package for practice questions');
      } else {
        toast.error('Failed to generate questions. Please try again.');
      }
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answer) => {
    if (showResults) return;
    setSelectedAnswers(prev => ({ ...prev, [questionIndex]: answer }));
  };

  const checkAnswers = () => setShowResults(true);

  const getScore = () => {
    let correct = 0;
    questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.correct_answer) correct++;
    });
    return correct;
  };

  const getStatusBadge = (status) => {
    if (status === 'confirmed') {
      return <span className="flex items-center gap-1 text-green-600 text-sm"><CheckCircle className="w-4 h-4" /> Confirmed</span>;
    } else if (status === 'cancelled') {
      return <span className="flex items-center gap-1 text-red-600 text-sm"><XCircle className="w-4 h-4" /> Cancelled</span>;
    }
    return <span className="flex items-center gap-1 text-yellow-600 text-sm"><AlertCircle className="w-4 h-4" /> Pending</span>;
  };

  const upcomingSessions = useMemo(
    () => sessions.filter(s => s.status !== 'cancelled' && new Date(s.date) >= new Date()),
    [sessions]
  );

  // Not subscribed and no credits at all
  if (!isSubscribed && !hasPlatformAccess && !hasSessionCredits) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-6" data-testid="dashboard-locked">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-[#002FA7]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[#002FA7]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0A0A0A] mb-4">Get Started</h1>
          <p className="text-[#4B5563] mb-8">
            Purchase a package to access live sessions, video library, 
            AI study tools, and more. Plans start at just $15.
          </p>
          <Link to="/pricing">
            <Button className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm px-8 py-6 font-bold" data-testid="view-pricing-btn">
              View Pricing Plans
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] py-6 md:py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-xl md:text-3xl font-bold text-[#0A0A0A]" data-testid="dashboard-welcome">
            Welcome back, {user?.name}
          </h1>
          <p className="text-[#4B5563] mt-1">
            {isSubscribed ? (
              <span className="inline-flex items-center gap-1 text-green-600">
                <CheckCircle className="w-4 h-4" /> Monthly Subscriber
              </span>
            ) : (
              <span className="inline-flex items-center flex-wrap gap-2">
                {credits?.session_credits > 0 && (
                  <span className="inline-flex items-center gap-1 bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 rounded text-xs font-medium" data-testid="session-credits-badge">
                    {credits.session_credits} session credit{credits.session_credits !== 1 ? 's' : ''}
                  </span>
                )}
                {credits?.group_credits === -1 && (
                  <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium">
                    Unlimited Groups
                  </span>
                )}
                {credits?.group_credits > 0 && (
                  <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium">
                    {credits.group_credits} group credit{credits.group_credits !== 1 ? 's' : ''}
                  </span>
                )}
                {credits?.has_platform_access && (
                  <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                    <CheckCircle className="w-3 h-3" /> Platform Access
                  </span>
                )}
              </span>
            )}
            {' '}• Book sessions, watch videos, and use AI tools.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white border border-[#E5E7EB] p-1 rounded-sm overflow-x-auto flex w-full">
            <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-overview">
              <BookOpen className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="book" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-book">
              <Calendar className="w-4 h-4 mr-2" />
              Book Session
            </TabsTrigger>
            <TabsTrigger value="videos" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-videos">
              <BookOpen className="w-4 h-4 mr-2" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="ai" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-ai">
              <Sparkles className="w-4 h-4 mr-2" />
              AI Assistant
            </TabsTrigger>
            <TabsTrigger value="practice" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-practice">
              <Brain className="w-4 h-4 mr-2" />
              Practice
            </TabsTrigger>
            <TabsTrigger value="progress" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-progress">
              <TrendingUp className="w-4 h-4 mr-2" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-sm data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="dashboard-widget">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#0A0A0A]">Upcoming Sessions</h3>
                  <Calendar className="w-5 h-5 text-[#002FA7]" />
                </div>
                <p className="text-3xl font-black text-[#002FA7]">{upcomingSessions.length}</p>
                <p className="text-sm text-[#4B5563]">sessions scheduled</p>
              </div>
              <div className="dashboard-widget">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#0A0A0A]">Videos Watched</h3>
                  <Video className="w-5 h-5 text-[#E53935]" />
                </div>
                <p className="text-3xl font-black text-[#E53935]" data-testid="watched-count">{watchedVideos.size}/{videos.length}</p>
                <div className="mt-2 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div className="h-full bg-[#E53935] rounded-full transition-all" style={{ width: `${videos.length > 0 ? Math.round((watchedVideos.size / videos.length) * 100) : 0}%` }} />
                </div>
              </div>
              <div className="dashboard-widget cursor-pointer hover:border-[#002FA7] transition-colors" onClick={() => setActiveTab('ai')}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#0A0A0A]">AI Assistant</h3>
                  <Sparkles className="w-5 h-5 text-[#FFC107]" />
                </div>
                <p className="text-sm text-[#4B5563] mb-2">Get instant help</p>
                <Button size="sm" className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">Start Chat</Button>
              </div>
              <div className="dashboard-widget cursor-pointer hover:border-[#002FA7] transition-colors" onClick={() => setActiveTab('practice')}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#0A0A0A]">Practice Tests</h3>
                  <Brain className="w-5 h-5 text-[#002FA7]" />
                </div>
                <p className="text-sm text-[#4B5563] mb-2">AI-generated questions</p>
                <Button size="sm" className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">Practice Now</Button>
              </div>
            </div>

            {/* Subscription Management */}
            {subscriptionStatus && (
              <div className="dashboard-widget" data-testid="subscription-management">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-[#0A0A0A] mb-1">Subscription</h3>
                    <p className="text-sm text-[#4B5563]">
                      {subscriptionStatus.status === 'active' ? (
                        <>Active &middot; ${subscriptionStatus.monthly_rate}/month {subscriptionStatus.end_date && <>&middot; Renews {new Date(subscriptionStatus.end_date).toLocaleDateString()}</>}</>
                      ) : subscriptionStatus.status === 'cancelled' ? (
                        <>Cancelled {subscriptionStatus.end_date && <>&middot; Access until {new Date(subscriptionStatus.end_date).toLocaleDateString()}</>}</>
                      ) : (
                        'Inactive'
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded-sm font-medium ${subscriptionStatus.status === 'active' ? 'bg-green-100 text-green-700' : subscriptionStatus.status === 'cancelled' ? 'bg-yellow-100 text-yellow-700' : 'bg-[#F3F4F6] text-[#4B5563]'}`} data-testid="subscription-status-badge">
                      {subscriptionStatus.status === 'active' ? 'Active' : subscriptionStatus.status === 'cancelled' ? 'Cancelled' : 'Inactive'}
                    </span>
                    {subscriptionStatus.status === 'active' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                        onClick={handleCancelSubscription}
                        disabled={cancellingSubscription}
                        data-testid="cancel-subscription-btn"
                      >
                        {cancellingSubscription ? 'Cancelling...' : 'Cancel'}
                      </Button>
                    )}
                    {subscriptionStatus.status === 'cancelled' && (
                      <Button
                        size="sm"
                        className="rounded-sm bg-[#002FA7] hover:bg-[#001B63]"
                        onClick={handleReactivateSubscription}
                        data-testid="reactivate-subscription-btn"
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Upcoming Sessions List */}
            <div className="dashboard-widget">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-[#0A0A0A]">Upcoming Sessions</h3>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setActiveTab('book')}>
                  Book New Session
                </Button>
              </div>
              {sessionsLoading ? (
                <div className="flex justify-center py-8"><div className="spinner"></div></div>
              ) : upcomingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-[#4B5563] mb-4">No upcoming sessions</p>
                  <Button className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm" onClick={() => setActiveTab('book')}>
                    Book Your First Session
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingSessions.slice(0, 5).map((session) => (
                    <div key={session.id} className="flex items-center justify-between p-4 border border-[#E5E7EB] rounded-sm">
                      <div>
                        <p className="font-medium text-[#0A0A0A]">{session.subject}</p>
                        <p className="text-sm text-[#4B5563]">
                          {format(new Date(session.date), 'MMMM d, yyyy')} • {session.time_slot}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {getStatusBadge(session.status)}
                        {session.meeting_link && (
                          <a href={session.meeting_link} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">
                              <Video className="w-4 h-4 mr-1" /> Join
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Book Session Tab */}
          <TabsContent value="book" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              <div className="md:col-span-5 dashboard-widget">
                <h3 className="font-bold text-[#0A0A0A] mb-4">Select a Date</h3>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                  className="rounded-sm"
                  data-testid="booking-calendar"
                />
              </div>
              <div className="md:col-span-7 dashboard-widget">
                <h3 className="font-bold text-[#0A0A0A] mb-4">
                  Available Times for {format(selectedDate, 'MMMM d, yyyy')}
                </h3>
                {slotsLoading ? (
                  <div className="flex justify-center py-8"><div className="spinner"></div></div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-[#4B5563] py-8">No available slots for this date.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        className={`time-slot ${selectedSlot === slot ? 'selected' : ''}`}
                        data-testid={`time-slot-${slot.replace(/\s/g, '-')}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                )}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Subject</label>
                  <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                    <SelectTrigger className="rounded-sm" data-testid="session-subject">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  className="w-full bg-[#002FA7] hover:bg-[#001B63] rounded-sm py-6 font-bold"
                  onClick={() => setShowBookingDialog(true)}
                  disabled={!selectedSlot}
                  data-testid="book-session-btn"
                >
                  Book Session
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Videos / Curriculum Tab */}
          <TabsContent value="videos" className="space-y-6">
            {/* Category Filter */}
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${categoryFilter === 'all' ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'}`}
                data-testid="filter-all"
              >
                All Courses
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${categoryFilter === cat ? 'bg-[#002FA7] text-white' : 'bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'}`}
                  data-testid={`filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {videosLoading || quizzesLoading ? (
              <div className="flex justify-center py-8"><div className="spinner"></div></div>
            ) : Object.keys(moduleData).length === 0 ? (
              <div className="dashboard-widget">
                <p className="text-[#4B5563] py-8 text-center">No content available for this category.</p>
              </div>
            ) : (
              Object.entries(moduleData).map(([moduleName, moduleInfo]) => (
                <div key={moduleName} className="dashboard-widget" data-testid={`module-${moduleName.replace(/\s/g, '-')}`}>
                  {/* Module Header */}
                  <button
                    onClick={() => toggleModule(moduleName)}
                    className="w-full flex items-center justify-between pb-4 border-b border-[#E5E7EB] mb-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-[#002FA7]" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-bold text-[#0A0A0A]">{moduleName}</h3>
                        <p className="text-sm text-[#4B5563]">
                          {moduleInfo.videos.length} video{moduleInfo.videos.length !== 1 ? 's' : ''}
                          {moduleInfo.quiz ? ' + Quiz' : ''}
                          {moduleInfo.quiz?.worksheet ? ' + Worksheet' : ''}
                          {watchSummary?.by_module?.[moduleName] && (
                            <> &middot; {watchSummary.by_module[moduleName].watched}/{watchSummary.by_module[moduleName].total} watched</>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getModuleProgress(moduleName) && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-sm font-medium flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> {getModuleProgress(moduleName).score_percentage}%
                        </span>
                      )}
                      <span className="text-xs bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm font-medium">
                        {moduleInfo.category}
                      </span>
                      {expandedModules[moduleName] !== false ? (
                        <ChevronUp className="w-5 h-5 text-[#4B5563]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[#4B5563]" />
                      )}
                    </div>
                  </button>

                  {/* Module Content (expanded by default) */}
                  {expandedModules[moduleName] !== false && (
                    <div className="space-y-6">
                      {/* Videos */}
                      <div>
                        <h4 className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Video className="w-4 h-4" /> Videos
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {moduleInfo.videos.sort((a, b) => a.order - b.order).map((video) => {
                            const isWatched = watchedVideos.has(video.id);
                            return (
                              <div
                                key={video.id}
                                className={`flex items-start gap-4 p-3 border rounded-sm hover:shadow-md transition-all group ${isWatched ? 'border-green-200 bg-green-50/30' : 'border-[#E5E7EB]'}`}
                                data-testid={`video-card-${video.id}`}
                              >
                                <a
                                  href={video.video_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => markVideoWatched(video.id)}
                                  className="relative w-32 h-20 flex-shrink-0 rounded-sm overflow-hidden bg-[#F3F4F6]"
                                >
                                  <img
                                    src={video.thumbnail_url || 'https://via.placeholder.com/160x90?text=Video'}
                                    alt={video.title}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Play className="w-8 h-8 text-white" />
                                  </div>
                                  {isWatched && (
                                    <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                      <CheckCircle className="w-3 h-3 text-white" />
                                    </div>
                                  )}
                                </a>
                                <div className="flex-1 min-w-0">
                                  <a href={video.video_url} target="_blank" rel="noopener noreferrer" onClick={() => markVideoWatched(video.id)}>
                                    <h5 className="font-medium text-[#0A0A0A] text-sm hover:text-[#002FA7] transition-colors">{video.title}</h5>
                                  </a>
                                  <p className="text-xs text-[#4B5563] mt-1 line-clamp-2">{video.description}</p>
                                  <div className="flex items-center gap-3 mt-2">
                                    <span className="text-xs text-[#9CA3AF] flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {video.duration_minutes} min
                                    </span>
                                    {!isWatched ? (
                                      <button
                                        onClick={() => markVideoWatched(video.id)}
                                        className="text-xs text-[#002FA7] hover:underline"
                                        data-testid={`mark-watched-${video.id}`}
                                      >
                                        Mark watched
                                      </button>
                                    ) : (
                                      <span className="text-xs text-green-600 flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Watched
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Quiz Section */}
                      {moduleInfo.quiz && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider mb-3 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4" /> Module Quiz
                          </h4>
                          <div className="p-4 border border-[#E5E7EB] rounded-sm bg-[#F9FAFB]">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-medium text-[#0A0A0A]">{moduleInfo.quiz.title}</h5>
                                <p className="text-sm text-[#4B5563]">{moduleInfo.quiz.questions.length} questions</p>
                              </div>
                              <Button
                                onClick={() => startQuiz(moduleInfo.quiz)}
                                className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
                                data-testid={`start-quiz-${moduleInfo.quiz.id}`}
                              >
                                <Brain className="w-4 h-4 mr-2" /> Take Quiz
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Worksheet Section */}
                      {moduleInfo.quiz?.worksheet && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#4B5563] uppercase tracking-wider mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Practice Worksheet
                          </h4>
                          <div className="p-4 border border-[#E5E7EB] rounded-sm">
                            <p className="text-sm text-[#4B5563] mb-3">{moduleInfo.quiz.worksheet.description || moduleInfo.quiz.worksheet.instructions}</p>
                            {moduleInfo.quiz.worksheet.sections ? (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {moduleInfo.quiz.worksheet.sections.map((section, idx) => (
                                  <div key={idx} className="p-3 bg-[#F9FAFB] rounded-sm border border-[#E5E7EB]">
                                    <p className="font-medium text-sm text-[#0A0A0A]">{section.title}</p>
                                    <p className="text-xs text-[#4B5563]">{section.count} problems</p>
                                  </div>
                                ))}
                              </div>
                            ) : moduleInfo.quiz.worksheet.problems ? (
                              <div className="space-y-2">
                                {moduleInfo.quiz.worksheet.problems.map((problem, idx) => (
                                  <div key={idx} className="p-3 bg-[#F9FAFB] rounded-sm border border-[#E5E7EB]">
                                    <p className="text-sm text-[#0A0A0A]">{problem}</p>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </TabsContent>

          {/* AI Assistant Tab */}
          <TabsContent value="ai" className="space-y-6">
            <DashboardChat
              subjects={SUBJECTS}
              chatMessages={chatMessages}
              chatInput={chatInput}
              setChatInput={setChatInput}
              chatLoading={chatLoading}
              chatSubject={chatSubject}
              setChatSubject={setChatSubject}
              sendChatMessage={sendChatMessage}
            />
          </TabsContent>

          {/* Practice Questions Tab */}
          <TabsContent value="practice" className="space-y-6">
            <DashboardPractice
              subjects={SUBJECTS}
              topics={TOPICS}
              practiceSubject={practiceSubject}
              setPracticeSubject={setPracticeSubject}
              practiceTopic={practiceTopic}
              setPracticeTopic={setPracticeTopic}
              practiceDifficulty={practiceDifficulty}
              setPracticeDifficulty={setPracticeDifficulty}
              practiceCount={practiceCount}
              setPracticeCount={setPracticeCount}
              questions={questions}
              questionsLoading={questionsLoading}
              selectedAnswers={selectedAnswers}
              handleAnswerSelect={handleAnswerSelect}
              showResults={showResults}
              checkAnswers={checkAnswers}
              getScore={getScore}
              generateQuestions={generateQuestions}
            />
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress" className="space-y-6" data-testid="progress-tab-content">
            <DashboardProgress
              progressSummary={progressSummary}
              progressLoading={progressLoading}
              quizzes={quizzes}
              getModuleProgress={getModuleProgress}
              setActiveTab={setActiveTab}
            />
          </TabsContent>

          {/* Session History Tab */}
          <TabsContent value="history" className="space-y-6" data-testid="history-tab-content">
            <DashboardHistory
              sessions={sessions}
              sessionsLoading={sessionsLoading}
              getStatusBadge={getStatusBadge}
              setActiveTab={setActiveTab}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Confirmation Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Confirm Your Session</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#4B5563]">Tutor</span>
              <span className="font-medium">Ace Black</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#4B5563]">Date</span>
              <span className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#4B5563]">Time</span>
              <span className="font-medium">{selectedSlot}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
              <span className="text-[#4B5563]">Subject</span>
              <span className="font-medium">{selectedSubject}</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">Notes (optional)</label>
              <Textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="Any specific topics you'd like to cover?"
                className="rounded-sm"
                data-testid="session-notes"
              />
            </div>
            <p className="text-sm text-[#4B5563]">
              <CheckCircle className="w-4 h-4 inline mr-1 text-green-600" />
              You'll receive an email reminder 24 hours before this session.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingDialog(false)} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={handleBookSession}
              disabled={bookingLoading}
              className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
              data-testid="confirm-session-btn"
            >
              {bookingLoading ? 'Booking...' : 'Confirm Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={!!activeQuiz} onOpenChange={(open) => { if (!open) { setActiveQuiz(null); setQuizAnswers({}); setQuizResults(null); } }}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" data-testid="quiz-dialog-title">
              {activeQuiz?.title}
            </DialogTitle>
          </DialogHeader>

          {!quizResults ? (
            <div className="py-4 space-y-5">
              {activeQuiz?.questions.map((q, qIdx) => (
                <div key={qIdx} className="p-4 border border-[#E5E7EB] rounded-sm" data-testid={`quiz-question-${qIdx}`}>
                  <p className="font-medium text-[#0A0A0A] mb-3">{qIdx + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oIdx) => {
                      const letter = opt.charAt(0);
                      const isSelected = quizAnswers[String(qIdx)] === letter;
                      return (
                        <button
                          key={oIdx}
                          onClick={() => setQuizAnswers(prev => ({ ...prev, [String(qIdx)]: letter }))}
                          className={`w-full text-left p-3 rounded-sm border transition-colors ${
                            isSelected ? 'bg-[#002FA7]/10 border-[#002FA7]' : 'border-[#E5E7EB] hover:border-[#002FA7]'
                          }`}
                          data-testid={`quiz-option-${qIdx}-${letter}`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <Button
                onClick={submitQuiz}
                disabled={quizSubmitting || Object.keys(quizAnswers).length < (activeQuiz?.questions.length || 0)}
                className="w-full bg-[#002FA7] hover:bg-[#001B63] rounded-sm py-5 font-bold"
                data-testid="submit-quiz-btn"
              >
                {quizSubmitting ? 'Submitting...' : 'Submit Quiz'}
              </Button>
            </div>
          ) : (
            <div className="py-4 space-y-5">
              {/* Score Card */}
              <div className={`p-6 rounded-sm text-center ${quizResults.score_percentage >= 80 ? 'bg-green-50 border border-green-200' : quizResults.score_percentage >= 50 ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'}`}>
                <Award className={`w-12 h-12 mx-auto mb-2 ${quizResults.score_percentage >= 80 ? 'text-green-600' : quizResults.score_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`} />
                <p className="text-3xl font-black">{quizResults.score_percentage}%</p>
                <p className="text-sm text-[#4B5563]">{quizResults.correct_answers}/{quizResults.total_questions} correct</p>
              </div>
              {/* Results Detail */}
              {quizResults.results.map((r, idx) => (
                <div key={idx} className={`p-4 border rounded-sm ${r.is_correct ? 'border-green-300 bg-green-50/50' : 'border-red-300 bg-red-50/50'}`} data-testid={`quiz-result-${idx}`}>
                  <div className="flex items-start gap-2">
                    {r.is_correct ? <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />}
                    <div>
                      <p className="font-medium text-[#0A0A0A]">{idx + 1}. {r.question}</p>
                      {!r.is_correct && <p className="text-sm text-red-600 mt-1">Your answer: {r.your_answer || 'Not answered'}</p>}
                      <p className="text-sm text-green-700 mt-1">Correct: {r.correct_answer}</p>
                      <p className="text-sm text-[#4B5563] mt-2 italic">{r.explanation}</p>
                    </div>
                  </div>
                </div>
              ))}
              <Button
                onClick={() => { setActiveQuiz(null); setQuizAnswers({}); setQuizResults(null); }}
                className="w-full bg-[#002FA7] hover:bg-[#001B63] rounded-sm"
                data-testid="close-quiz-results"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
